/**
 * GlobalModes — toolbar for chat-wide settings (slow / sub-only / follower-only / emote-only).
 * Mod-only.
 */

import { useEffect, useState } from 'react';
import {
  useModActions,
  type ChatSettings,
  type ChatSettingsPatch,
} from '../../hooks/useModActions.js';
import type { ApiError } from '../../lib/api.js';

const FOLLOWER_DURATIONS: Array<{ label: string; minutes: number }> = [
  { label: '0 min', minutes: 0 },
  { label: '10 min', minutes: 10 },
  { label: '30 min', minutes: 30 },
  { label: '1 h', minutes: 60 },
  { label: '24 h', minutes: 1440 },
];

const SLOW_TIMES: Array<{ label: string; seconds: number }> = [
  { label: '3s', seconds: 3 },
  { label: '5s', seconds: 5 },
  { label: '10s', seconds: 10 },
  { label: '30s', seconds: 30 },
  { label: '60s', seconds: 60 },
];

export default function GlobalModes() {
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { getChatSettings, patchChatSettings } = useModActions();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await getChatSettings();
        if (!cancelled) setSettings(s);
      } catch (err) {
        if (!cancelled) {
          const apiErr = err as ApiError;
          setErrorMsg(`Fehler beim Laden: ${apiErr?.status ?? '?'}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getChatSettings]);

  async function applyPatch(patch: ChatSettingsPatch): Promise<void> {
    if (busy) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      const updated = await patchChatSettings(patch);
      if (updated) setSettings(updated);
    } catch (err) {
      const apiErr = err as ApiError;
      setErrorMsg(`Fehler: ${apiErr?.status ?? '?'}`);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="text-xs text-slate-500 px-2">Lade Modes…</div>;
  if (!settings) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-2.5 py-[5px] border-b border-slate-800 bg-slate-900/40 text-xs">
      <span className="text-slate-400 font-semibold mr-1">Modes:</span>

      <Toggle
        label="Sub-only"
        enabled={settings.subscriber_mode}
        disabled={busy}
        onToggle={(v) => applyPatch({ subscriber_mode: v })}
      />
      <Toggle
        label="Emote-only"
        enabled={settings.emote_mode}
        disabled={busy}
        onToggle={(v) => applyPatch({ emote_mode: v })}
      />
      <Toggle
        label="Unique"
        enabled={settings.unique_chat_mode}
        disabled={busy}
        onToggle={(v) => applyPatch({ unique_chat_mode: v })}
      />

      <div className="flex items-center gap-1">
        <Toggle
          label="Slow"
          enabled={settings.slow_mode}
          disabled={busy}
          onToggle={(v) => applyPatch({ slow_mode: v })}
        />
        {settings.slow_mode && (
          <select
            disabled={busy}
            value={settings.slow_mode_wait_time ?? 30}
            onChange={(e) =>
              void applyPatch({ slow_mode: true, slow_mode_wait_time: Number(e.target.value) })
            }
            className="bg-slate-800 border border-slate-700 rounded px-1 py-0.5"
          >
            {SLOW_TIMES.map((t) => (
              <option key={t.seconds} value={t.seconds}>
                {t.label}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Toggle
          label="Follow-only"
          enabled={settings.follower_mode}
          disabled={busy}
          onToggle={(v) => applyPatch({ follower_mode: v })}
        />
        {settings.follower_mode && (
          <select
            disabled={busy}
            value={settings.follower_mode_duration ?? 0}
            onChange={(e) =>
              void applyPatch({ follower_mode: true, follower_mode_duration: Number(e.target.value) })
            }
            className="bg-slate-800 border border-slate-700 rounded px-1 py-0.5"
          >
            {FOLLOWER_DURATIONS.map((d) => (
              <option key={d.minutes} value={d.minutes}>
                {d.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {errorMsg && <span className="text-red-400 ml-2">{errorMsg}</span>}
    </div>
  );
}

function Toggle({
  label,
  enabled,
  disabled,
  onToggle,
}: {
  label: string;
  enabled: boolean;
  disabled: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onToggle(!enabled)}
      className={`px-2 py-0.5 rounded border ${
        enabled
          ? 'bg-purple-700/40 border-purple-600 text-purple-200'
          : 'bg-slate-800 border-slate-700 text-slate-400'
      } disabled:opacity-50`}
    >
      {label}
    </button>
  );
}
